/**
 * 错误处理工具函数
 */
import { ElMessage, ElMessageBox } from 'element-plus';
import { appConfig } from '@/config';
import type { AppError as AppErrorType, ErrorHandlerOptions, ErrorToastOptions, ErrorLog } from '@/types/error';
import { ErrorType, ERROR_CODE_MAPPING, HTTP_STATUS_TO_ERROR_TYPE } from '@/types/error';

/**
 * 自定义错误类
 */
export class AppError extends Error implements AppErrorType {
  type: ErrorType;
  code?: string | number;
  details?: any;
  timestamp: number;

  constructor(
    message: string,
    type: ErrorType = 'UNKNOWN' as ErrorType,
    code?: string | number,
    details?: any
  ) {
    super(message);
    this.name = 'AppError';
    this.type = type;
    this.code = code;
    this.details = details;
    this.timestamp = Date.now();
    
    // 确保原型链正确设置
    if (Object.setPrototypeOf) {
      Object.setPrototypeOf(this, AppError.prototype);
    } else {
      (this as any).__proto__ = AppError.prototype;
    }
  }
}

/**
 * 格式化错误信息
 * @param error 错误对象
 * @returns 格式化后的错误信息
 */
export const formatError = (error: any): string => {
  if (!error) {
    return '未知错误';
  }

  if (error instanceof AppError) {
    return error.message;
  }

  if (error.response && error.response.data) {
    // API 响应错误
    if (error.response.data.message) {
      return error.response.data.message;
    }
    if (error.response.data.error) {
      return error.response.data.error;
    }
  }

  if (error.message) {
    return error.message;
  }

  return String(error);
};

/**
 * 获取错误类型
 * @param error 错误对象
 * @returns 错误类型
 */
export const getErrorType = (error: any): ErrorType => {
  if (!error) {
    return ErrorType.UNKNOWN;
  }

  if (error instanceof AppError) {
    return error.type;
  }

  if (error.response) {
    const status = error.response.status;
    if (status === 401) {
      return ErrorType.AUTHENTICATION;
    } else if (status === 403) {
      return ErrorType.PERMISSION;
    } else if (status === 404) {
      return ErrorType.NOT_FOUND;
    } else if (status >= 500) {
      return ErrorType.SERVER;
    } else if (status >= 400) {
      return ErrorType.CLIENT;
    }
  } else if (error.request) {
    // 请求已发送但未收到响应
    return ErrorType.NETWORK;
  }

  return ErrorType.UNKNOWN;
};

/**
 * 记录错误日志
 * @param error 错误对象
 * @param context 错误上下文信息
 */
export const logError = (error: any, context?: string): void => {
  if (!error) {
    return;
  }

  const timestamp = new Date().toISOString();
  const errorType = getErrorType(error);
  const errorMessage = formatError(error);
  
  const logEntry = {
    timestamp,
    type: errorType,
    message: errorMessage,
    context,
    error: error instanceof Error ? {
      name: error.name,
      message: error.message,
      stack: error.stack,
    } : error,
  };

  // 在开发环境下打印详细日志
  if (appConfig.env.debug) {
    console.error('[App Error]', logEntry);
  } else {
    // 生产环境简化日志
    console.error(`[App Error] ${timestamp} ${errorType}: ${errorMessage}`);
  }

  // 存储错误日志（可以根据需要扩展为上报到服务器）
  storeErrorLog(logEntry);
};

/**
 * 存储错误日志到本地存储
 * @param logEntry 日志条目
 */
export const storeErrorLog = (logEntry: any): void => {
  try {
    const existingLogs = JSON.parse(localStorage.getItem('app_error_logs') || '[]');
    
    // 限制日志数量，防止本地存储溢出
    const maxLogs = 50;
    if (existingLogs.length >= maxLogs) {
      existingLogs.shift();
    }
    
    existingLogs.push(logEntry);
    localStorage.setItem('app_error_logs', JSON.stringify(existingLogs));
  } catch (storageError) {
    console.error('Failed to store error log:', storageError);
  }
};

/**
 * 显示错误提示
 * @param error 错误对象
 * @param options 选项
 */
export const showError = (error: any, options?: {
  title?: string;
  showDetail?: boolean;
  onConfirm?: () => void;
}): void => {
  const { title = '操作失败', showDetail = false, onConfirm } = options || {};
  const errorMessage = formatError(error);
  const errorType = getErrorType(error);
  
  // 根据错误类型选择不同的提示方式
  switch (errorType) {
    case ErrorType.AUTHENTICATION:
      // 认证错误，需要重新登录
      ElMessageBox.confirm('您的登录已过期，请重新登录', '认证失败', {
        confirmButtonText: '重新登录',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        // 跳转到登录页
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        if (onConfirm) onConfirm();
      });
      break;
    
    case ErrorType.PERMISSION:
      // 权限错误，显示警告对话框
      ElMessageBox.alert('您没有权限执行此操作', '权限不足', {
        confirmButtonText: '确定',
        type: 'warning',
      }).then(() => {
        if (onConfirm) onConfirm();
      });
      break;
    
    case ErrorType.NETWORK:
      // 网络错误，显示详细信息
      ElMessageBox.alert(
        `网络连接失败，请检查您的网络设置。\n\n${showDetail ? errorMessage : ''}`, 
        '网络错误',
        {
          confirmButtonText: '确定',
          type: 'error',
        }
      ).then(() => {
        if (onConfirm) onConfirm();
      });
      break;
    
    default:
      // 其他错误，简单提示
      if (showDetail) {
        ElMessageBox.alert(errorMessage, title, {
          confirmButtonText: '确定',
          type: 'error',
        }).then(() => {
          if (onConfirm) onConfirm();
        });
      } else {
        ElMessage.error(errorMessage);
        if (onConfirm) onConfirm();
      }
  }
};

/**
 * 显示成功提示
 * @param message 提示信息
 */
export const showSuccess = (message: string): void => {
  ElMessage.success(message);
};

/**
 * 显示警告提示
 * @param message 警告信息
 */
export const showWarning = (message: string): void => {
  ElMessage.warning(message);
};

/**
 * 错误处理包装器 - 用于包装异步函数
 * @param fn 异步函数
 * @param options 错误处理选项
 * @returns 包装后的函数
 */
export const withErrorHandling = <T extends (...args: any[]) => Promise<any>>(fn: T, options?: {
  showError?: boolean;
  customMessage?: string;
  onError?: (error: any) => void;
}): ((...args: Parameters<T>) => Promise<ReturnType<T> | undefined>) => {
  const { showError = true, customMessage, onError } = options || {};
  
  return async (...args: Parameters<T>): Promise<ReturnType<T> | undefined> => {
    try {
      return await fn(...args);
    } catch (error) {
      const errorMessage = customMessage || formatError(error);
      
      // 记录错误
      logError(error, `Function: ${fn.name || 'anonymous'}`);
      
      // 调用自定义错误处理
      if (onError) {
        onError(error);
      }
      
      // 显示错误提示
      if (showError) {
        ElMessage.error(errorMessage);
      }
      
      // 返回undefined表示操作失败
      return undefined;
    }
  };
};

/**
 * 全局错误处理注册函数
 */
export const setupGlobalErrorHandlers = (): void => {
  // 捕获未处理的Promise拒绝
  window.addEventListener('unhandledrejection', (event) => {
    event.preventDefault();
    logError(event.reason, 'Unhandled Promise Rejection');
    
    // 对于重要的Promise拒绝，可以显示错误提示
    // showError(event.reason, { showDetail: appConfig.env.debug });
  });

  // 捕获全局错误
  window.addEventListener('error', (event) => {
    event.preventDefault();
    logError(event.error, 'Global Error');
    
    // 对于重要的全局错误，可以显示错误提示
    // 注意：避免过度使用，以免在页面崩溃时弹出过多提示
    // if (!event.error.toString().includes('ResizeObserver loop limit exceeded')) {
    //   showError(event.error, { showDetail: appConfig.env.debug });
    // }
  });
};

/**
 * 获取错误日志列表
 */
export const getErrorLogs = (): any[] => {
  try {
    return JSON.parse(localStorage.getItem('app_error_logs') || '[]');
  } catch (error) {
    console.error('Failed to retrieve error logs:', error);
    return [];
  }
};

/**
 * 清空错误日志
 */
export const clearErrorLogs = (): void => {
  try {
    localStorage.removeItem('app_error_logs');
  } catch (error) {
    console.error('Failed to clear error logs:', error);
  }
};

export default {
  AppError,
  formatError,
  getErrorType,
  logError,
  showError,
  showSuccess,
  showWarning,
  withErrorHandling,
  setupGlobalErrorHandlers,
  getErrorLogs,
  clearErrorLogs,
};